/**
 * Asks Google for lasting, read-only access in a consent popup (MHS-1024) and hands back the one-time code; the
 * backend exchanges it for a refresh token. Only rendered on Google deployments (it needs GoogleOAuthProvider), and
 * only where the backend says consent is "google_code".
 */

import { Button } from "@design";
import { useGoogleLogin, useGoogleOAuth } from "@react-oauth/google";
import type { ReactNode } from "react";

export function GoogleConsentButton({
  scopes,
  hint,
  loading,
  onCode,
  onError,
  children,
}: Readonly<{
  scopes: readonly string[];
  /** The signed-in person's email, so Google offers that account first. */
  hint?: string;
  loading?: boolean;
  onCode: (code: string) => void;
  /** "unavailable": Google's script didn't load; "failed": Google refused or the window was blocked. */
  onError: (reason: "unavailable" | "failed") => void;
  children: ReactNode;
}>) {
  const { scriptLoadedSuccessfully } = useGoogleOAuth();
  const requestCode = useGoogleLogin({
    flow: "auth-code",
    scope: scopes.join(" "),
    // Only what background runs need: not the write scopes the sign-in token carries.
    include_granted_scopes: false,
    hint,
    onSuccess: ({ code }) => onCode(code),
    onError: () => onError("failed"),
    onNonOAuthError: (error) => {
      // Closing the window is the person's choice; a blocked window needs telling.
      if (error.type === "popup_failed_to_open") onError("failed");
    },
  });
  return (
    <Button
      variant="primary"
      loading={loading}
      onClick={() => (scriptLoadedSuccessfully ? requestCode() : onError("unavailable"))}
    >
      {children}
    </Button>
  );
}
